'use client';

import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useRequestQueue } from '@web/queue/context';
import {
  useShopifyPet,
  useCartLineItemForPet,
  useMealPlansCombinations,
} from '@web/shopify';
import { useShopifyExtras } from '@web/shopify/useShopifyExtras';
import { getPronoun, PRONOUN_TYPES, joinWithOr } from '../../pets';
import { useReporter, useElevarReporter } from '../../reporter';
import { useToast } from '../../Toast';
import { useCampaign } from '../../campaign';
import {
  useExperiment,
  TREATS_FOR_LIFE,
  ONBOARDING_FLOW_UI,
  HIDE_MIXED,
  SHOW_GIFT_STEP,
  SHOW_QUIZ_ABANDONER_MENU,
  ONBOARDING_PUP_BREED_STEP,
  SHOW_GIFT_CHECKOUT_EXTENSION,
  STARTER_CRUNCH_VS_LOADING,
} from '../../experiments';
import {
  ADD_PUP_SUBPATH,
  EDIT_PET_SUBPATH,
  DELETE_PET_SUBPATH,
} from '../routing';
import { onboardingSessionStarted } from '../events';
import { useShopifyCartContext } from './ShopifyCartProvider';
import { isCompletePet } from './guard';
import { useMachine, onboardingMachine } from './state';
import { readCachedCart, writeCachedCart } from './cartCache';
import {
  writeFurthestStep,
  milestoneFromStateName,
  clearFurthestStep,
  readFurthestStep,
  bucketFromFurthestStep,
} from './furthestStep';
import { STATE, ACTIONS } from './constants';
import {
  resolveElevarCartTotal,
  resolveInitialLoadPet,
  resolveInitialLoadSourceCart,
  shouldDefaultShowPlanSelection,
  isReturningSession,
} from './helpers';

const initialContext = {};
export const OnboardingContext = createContext(initialContext);

export const OnboardingProvider = ({ children }) => {
  const router = useRouter();
  const reporter = useReporter();
  const elevarReporter = useElevarReporter();
  const { queueRequest } = useRequestQueue();
  const [showModal, setShowModal] = useState(false);
  const [subpath] = router.query.path ?? [];
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const isEditPet = subpath === EDIT_PET_SUBPATH;
  const sessionStartTaggedRef = useRef(false);
  const [showPlanSelection, setShowPlanSelection] = useState(
    shouldDefaultShowPlanSelection(subpath),
  );
  const {
    cart,
    attachExtras,
    attachFreeGifts,
    isLoading: isShopifyCartLoading,
    deletePetFromCart,
    isFetchingOfferFlags,
    allowFreeGiftSelection,
    ...cartApi
  } = useShopifyCartContext();
  const { campaignId } = useCampaign();
  const { showToast } = useToast();

  const pet_id = router.query?.pet_id;
  const isNewPet = pet_id === 'new_pet';
  const {
    pet,
    isLoading: petIsLoading,
    savePet,
  } = useShopifyPet(!isNewPet ? pet_id : undefined);

  const currentMachine = onboardingMachine;
  const [state, send, service] = useMachine(currentMachine);
  //if the pet already has a starterbox that means the user has already seen this page (edit pup)
  const completedPet = isCompletePet(pet);
  const editPetFlow = useRef(isEditPet);

  const { lineItemForPet: mealPlanForPet, addonsForPet } =
    useCartLineItemForPet(pet_id);

  // Add meal plan combinations hook
  const {
    combinationsByPlanType,
    isLoading: isCombinationsLoading,
    isValidating: isCombinationsValidating,
    refreshPlans,
  } = useMealPlansCombinations(completedPet ? pet_id : null);

  const {
    treats: treatProducts,
    supplements: supplementProducts,
    dentalChews,
    isLoading: isExtrasLoading,
    refreshExtras,
  } = useShopifyExtras(completedPet ? pet_id : null);

  // Update loading state to include combinations loading
  const isLoading =
    isShopifyCartLoading ||
    isCombinationsLoading ||
    isExtrasLoading ||
    petIsLoading;

  const goBack = () => {
    send(ACTIONS.goBack());
  };

  const goToLogin = () => {
    router.push(`/auth/login/`);
    showToast({
      status: 'success',
      message:
        'We found an account associated with your email. Please log in to access your account.',
    });
  };

  const { value: treatsForLife } = useExperiment(TREATS_FOR_LIFE, false);
  const { value: onboardingFlowUI } = useExperiment(
    ONBOARDING_FLOW_UI,
    'control',
  );

  const { value: hideMixed } = useExperiment(HIDE_MIXED, false);

  const { value: showGiftOnboardingStepFlag } = useExperiment(
    SHOW_GIFT_STEP,
    false,
  );

  const { value: showQuizAbandonerMenu } = useExperiment(
    SHOW_QUIZ_ABANDONER_MENU,
    false,
  );

  const { value: onboardingPupBreedStep } = useExperiment(
    ONBOARDING_PUP_BREED_STEP,
    false,
  );

  const { value: showGiftCheckoutExtension } = useExperiment(
    SHOW_GIFT_CHECKOUT_EXTENSION,
    false,
  );

  const crunchVsLoadingExperiment = useExperiment(
    STARTER_CRUNCH_VS_LOADING,
    'control',
  );

  const cachedCart = useMemo(() => readCachedCart(), []);

  useEffect(() => {
    if (!isShopifyCartLoading && cart?.cart_id) {
      writeCachedCart(cart);
    }
  }, [cart, isShopifyCartLoading]);

  useEffect(() => {
    if (subpath === ADD_PUP_SUBPATH) {
      clearFurthestStep();
    }
  }, [subpath]);

  useEffect(() => {
    const milestone = milestoneFromStateName(state?.name, {
      fromQuestion: state?.context?.fromQuestion,
    });
    if (milestone) {
      writeFurthestStep(milestone);
    }
  }, [state?.name, state?.context?.fromQuestion]);

  const showGiftOnboardingStep =
    showGiftOnboardingStepFlag &&
    !showGiftCheckoutExtension &&
    allowFreeGiftSelection;

  const showFreeGiftSelectionExtension =
    showGiftCheckoutExtension && allowFreeGiftSelection;

  useEffect(() => {
    if (state.name !== STATE.initial) return;

    const sourceCart = resolveInitialLoadSourceCart({
      showQuizAbandonerMenu,
      cachedCart,
      liveCart: cart,
      subpath,
    });

    if (!sourceCart) return;

    const { pet: resolvedPet, pet_id: resolvedPetId } = resolveInitialLoadPet({
      sourceCart,
      pet_id,
      pet,
    });

    send(
      ACTIONS.initialLoad({
        customer: sourceCart.customer,
        pets: sourceCart?.pets ?? [],
        pet: resolvedPet,
        pet_id: resolvedPetId,
        cart: sourceCart,
        flags: {
          onboardingFlowUI,
          hideMixed,
          showGiftOnboardingStep,
          showQuizAbandonerMenu,
          onboardingPupBreedStep,
        },
        isAddPet: subpath === ADD_PUP_SUBPATH,
        isEditPet,
        isDeletePet: subpath === DELETE_PET_SUBPATH,
        deletePetFromCart,
      }),
    );

    if (!sessionStartTaggedRef.current) {
      sessionStartTaggedRef.current = true;
      const furthestStep = readFurthestStep();
      reporter.tag(
        onboardingSessionStarted({
          isReturning: isReturningSession({ petId: resolvedPetId, subpath }),
          furthestStep,
          audienceGroup: bucketFromFurthestStep(furthestStep),
          inQuizAbandonerTest: showQuizAbandonerMenu,
        }),
      );
    }
  }, [
    state.name,
    cart,
    cachedCart,
    send,
    pet,
    pet_id,
    isLoading,
    petIsLoading,
    deletePetFromCart,
    isEditPet,
    subpath,
    treatsForLife,
    onboardingFlowUI,
    hideMixed,
    showGiftOnboardingStep,
    showQuizAbandonerMenu,
    onboardingPupBreedStep,
    reporter,
  ]);

  useEffect(() => {
    if (!cart) return;

    elevarReporter?.updateElevarCartSummary?.({
      cartTotal: resolveElevarCartTotal(cart),
    });
  }, [cart, elevarReporter]);

  // Track Next.js route transitions while GSSP is resolving on the destination page
  useEffect(() => {
    const handleStart = () => setIsRouteChanging(true);
    const handleComplete = () => setIsRouteChanging(false);
    const handleError = () => setIsRouteChanging(false);

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleError);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleError);
    };
  }, [router]);

  const values = {
    combinationsByPlanType,
    isCombinationsLoading,
    isCombinationsValidating,
    petIsLoading,
    refreshExtras,
    refreshPlans,
    pets: cart?.pets,
    pet,
    getPetPronoun: (perspective, type) =>
      getPronoun(pet.gender, {
        type:
          PRONOUN_TYPES[type.toString().toUpperCase()] || PRONOUN_TYPES.SUBJECT,
        perspective: perspective || 3,
      }),
    joinWithOr,
    savePet,
    cartApi,
    cart,
    cachedCart,
    customer: cart?.customer,
    mealPlanForPet,
    treatProducts,
    supplementProducts,
    dentalChews,
    attachExtras,
    attachFreeGifts,
    queueRequest,
    deletePetFromCart,
    setLocalSelection: () => {},
    send,
    service,
    pet_id: state.context.pet_id,
    fromQuestion: state.context.fromQuestion,
    interstitial: state.context.interstitial,
    selectedAddons: addonsForPet,
    stateKey: state.name,
    reporter,
    elevarReporter,
    showModal,
    setShowModal,
    goBack,
    goToLogin,
    isRouteChanging,
    pricePeriod: 'week',
    showPlanSelection,
    setShowPlanSelection,
    editPetFlow,
    treatsForLife,
    flags: {
      onboardingFlowUI,
      hideMixed,
      showGiftOnboardingStep,
      showFreeGiftSelectionExtension,
      onboardingPupBreedStep,
      crunchVsLoadingExperiment,
    },
    campaignId,
    isFetchingOfferFlags,
  };

  return (
    <OnboardingContext.Provider value={values}>
      {children}
    </OnboardingContext.Provider>
  );
};

OnboardingProvider.propTypes = {
  children: PropTypes.object.isRequired,
};
